import { motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showSubtitle?: boolean
  subtitle?: string
  className?: string
  animated?: boolean
}

export function Logo({ 
  size = 'md', 
  showSubtitle = true,
  subtitle,
  className = '',
  animated = true 
}: LogoProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const location = useLocation()
  
  // Animate on page change
  useEffect(() => {
    if (animated) {
      setShouldAnimate(true)
      const timer = setTimeout(() => setShouldAnimate(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [location.pathname, animated])
  
  // Animate on hover
  const handleMouseEnter = () => {
    if (animated) {
      setIsHovered(true)
      setShouldAnimate(true)
      setTimeout(() => {
        setIsHovered(false)
        setShouldAnimate(false)
      }, 2000)
    }
  }
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-2xl',
    lg: 'text-4xl',
    xl: 'text-6xl'
  }

  const subtitleSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-lg',
    xl: 'text-xl'
  }

  const LogoContent = () => (
    <div 
      className={`logo-container flex flex-col items-center cursor-pointer transition-all duration-300 hover:scale-105 ${className}`}
      onMouseEnter={handleMouseEnter}
    >
      {/* Main Logo Container */}
      <div className="relative">
        {/* Outer glow ring - enhanced on hover */}
        <div className={`absolute inset-0 bg-gradient-to-r from-cyan-400/20 via-blue-400/10 to-cyan-400/20 blur-lg rounded-full transition-all duration-300 ${isHovered ? 'from-cyan-400/30 via-blue-400/20 to-cyan-400/30' : ''}`}></div>
        
        {/* Inner glow - enhanced on hover */}
        <div className={`absolute inset-2 bg-cyan-400/5 blur-md rounded-full transition-all duration-300 ${isHovered ? 'bg-cyan-400/10' : ''}`}></div>
        
        {/* Logo content */}
        <div className="relative z-10 flex items-center space-x-1 px-2 py-1">
          {/* SWG Text */}
          <motion.div
            initial={shouldAnimate ? { scale: 0.8, opacity: 0, rotateY: -90 } : {}}
            animate={shouldAnimate ? { scale: 1, opacity: 1, rotateY: 0 } : {}}
            transition={shouldAnimate ? { duration: 0.6, ease: "easeOut" } : {}}
            className={`font-starjedi text-cyan-400 tracking-wider ${sizeClasses[size]} font-bold transition-all duration-300 ${isHovered ? 'text-cyan-300' : ''}`}
            style={{
              textShadow: isHovered 
                ? '0 0 25px rgba(34, 211, 238, 1), 0 0 50px rgba(34, 211, 238, 0.6), 0 0 75px rgba(34, 211, 238, 0.3)'
                : '0 0 20px rgba(34, 211, 238, 0.8), 0 0 40px rgba(34, 211, 238, 0.4), 0 0 60px rgba(34, 211, 238, 0.2)'
            }}
          >
            SWG
          </motion.div>
          
          {/* Colon with pulsing animation */}
          <motion.div
            initial={shouldAnimate ? { opacity: 0, scale: 0 } : {}}
            animate={shouldAnimate ? { opacity: 1, scale: 1 } : {}}
            transition={shouldAnimate ? { duration: 0.4, delay: 0.2 } : {}}
            className={`text-cyan-400 font-bold transition-all duration-300 ${isHovered ? 'animate-pulse text-cyan-300' : 'animate-pulse'}`}
            style={{ fontSize: '0.7em' }}
          >
            :
          </motion.div>
          
          {/* Origins Text */}
          <motion.div
            initial={shouldAnimate ? { x: 20, opacity: 0, rotateY: 90 } : {}}
            animate={shouldAnimate ? { x: 0, opacity: 1, rotateY: 0 } : {}}
            transition={shouldAnimate ? { duration: 0.6, delay: 0.3 } : {}}
            className={`font-starjedi text-cyan-300 tracking-wider ${sizeClasses[size]} font-bold transition-all duration-300 ${isHovered ? 'text-cyan-200' : ''}`}
            style={{
              textShadow: isHovered 
                ? '0 0 20px rgba(34, 211, 238, 0.8), 0 0 40px rgba(34, 211, 238, 0.5)'
                : '0 0 15px rgba(34, 211, 238, 0.6), 0 0 30px rgba(34, 211, 238, 0.3)'
            }}
          >
            ORIGINS
          </motion.div>
        </div>
        
        {/* Holographic scan lines - enhanced on hover */}
        <motion.div
          initial={shouldAnimate ? { opacity: 0 } : {}}
          animate={shouldAnimate ? { opacity: 1 } : {}}
          transition={shouldAnimate ? { duration: 0.6, delay: 0.5 } : {}}
          className={`absolute inset-0 pointer-events-none transition-all duration-300 ${isHovered ? 'opacity-100' : 'opacity-60'}`}
          style={{
            background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(34, 211, 238, 0.1) 2px, rgba(34, 211, 238, 0.1) 4px)'
          }}
        />
      </div>

      {/* Subtitle */}
      {showSubtitle && (
        <motion.div
          initial={shouldAnimate ? { y: 10, opacity: 0 } : {}}
          animate={shouldAnimate ? { y: 0, opacity: 1 } : {}}
          transition={shouldAnimate ? { duration: 0.4, delay: 0.6 } : {}}
          className={`text-gray-400 font-mono tracking-widest mt-2 ${subtitleSizeClasses[size]} text-center transition-all duration-300 ${isHovered ? 'text-cyan-400' : ''}`}
        >
          {subtitle || 'GALACTIC COMMAND'}
        </motion.div>
      )}

      {/* Decorative elements - Star Wars style */}
      <motion.div
        initial={shouldAnimate ? { scaleX: 0 } : {}}
        animate={shouldAnimate ? { scaleX: 1 } : {}}
        transition={shouldAnimate ? { duration: 0.6, delay: 0.8 } : {}}
        className="flex items-center justify-center mt-2 space-x-1"
      >
        <div className={`w-1 h-1 bg-cyan-400 rounded-full animate-pulse transition-all duration-300 ${isHovered ? 'bg-cyan-300' : ''}`}></div>
        <div className={`w-8 h-0.5 bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent transition-all duration-300 ${isHovered ? 'via-cyan-300/80' : ''}`}></div>
        <div className={`w-1 h-1 bg-cyan-400 rounded-full animate-pulse transition-all duration-300 ${isHovered ? 'bg-cyan-300' : ''}`} style={{animationDelay: '0.5s'}}></div>
      </motion.div>
    </div>
  )

  return <LogoContent />
}

// Compact version for headers
export function LogoCompact({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {/* SWG text */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 to-blue-400/10 blur-sm rounded"></div>
        <span className="relative z-10 font-starjedi text-cyan-400 text-lg tracking-wider font-bold">
          SWG
        </span>
      </div>
      
      {/* Colon with pulse */}
      <span className="text-cyan-400 font-bold animate-pulse">:</span>
      
      {/* Origins text */}
      <span className="font-starjedi text-cyan-300 text-lg tracking-wider font-bold">
        ORIGINS
      </span>
    </div>
  )
}

// Icon version for favicons/small spaces
export function LogoIcon({ className = '' }: { className?: string }) {
  return (
    <div className={`relative ${className}`}>
      <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/30 to-blue-400/20 blur-sm rounded-full"></div>
      <div className="relative z-10 w-8 h-8 flex items-center justify-center">
        <div className="font-starjedi text-cyan-400 text-sm font-bold">
          S
        </div>
      </div>
    </div>
  )
}
